import React, { ChangeEvent, HTMLInputTypeAttribute, memo } from "react";

import Label from "@/components/atoms/inputForms/Label2";
import InputText from "./InputText";
import { TextArea } from "./TextArea";

export type TextFieldProps = {
  id?: string;
  name?: string;
  value?: string | number;
  label?: string;
  type?: HTMLInputTypeAttribute | "area";
  defaultValue?: string;
  errorMessage?: string;
  error?: boolean;
  className?: string;
  inputClassName?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void | Promise<void>;
  placeholder?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  labelBorder?: boolean;
  spBorder?: boolean;
  ariaLabelledby?: string;
  inputMode?: NonNullable<JSX.IntrinsicElements["input"]["inputMode"]>;
  autoComplete?: string;
  disabled?: boolean;
  maxLength?: number;
  description?: string;
};

const TextFieldBase: React.FC<TextFieldProps> = (props) => {
  const {
    id,
    name,
    label,
    type = "text",
    value,
    defaultValue,
    ariaLabelledby,
    onChange,
    onBlur,
    onFocus,
    isReadOnly,
    isRequired,
    labelBorder,
    spBorder,
    error,
    errorMessage,
    placeholder,
    inputMode,
    autoComplete,
    className,
    disabled,
    maxLength,
    inputClassName,
    description,
  } = props;

  return (
    <div className={className}>
      {label && (
        <Label
          label={label}
          isRequired={isRequired}
          htmlFor={id}
          border={labelBorder}
          spBorder={spBorder}
          disabled={disabled}
          description={description}
        />
      )}
      <div className={`${labelBorder ? "mt-4" : "mt-1"}`}>
        {type === "area" ? (
          <TextArea
            id={id}
            className={inputClassName}
            name={name}
            value={value?.toString()}
            defaultValue={defaultValue}
            isReadOnly={isReadOnly}
            hasError={error}
            onChange={onChange}
            placeholder={placeholder}
            rows="8"
          />
        ) : (
          <InputText
            id={id}
            className={inputClassName}
            name={name}
            type={type}
            value={value}
            defaultValue={defaultValue}
            isReadOnly={isReadOnly}
            hasError={error}
            ariaLabelledby={ariaLabelledby}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            inputMode={inputMode}
            autoComplete={autoComplete}
            disabled={disabled}
            maxLength={maxLength}
          />
        )}
      </div>
      {errorMessage && <p className="text-error-msg">{errorMessage}</p>}
    </div>
  );
};

export const TextFormField = memo(TextFieldBase);
